import { FC, useEffect, useState } from 'react';
import { Button, Typography } from '@mui/material';

import { TableCell } from './TableCell';
import { Phase } from '../JourneyDetailPage';
import { FormattedMessage } from 'react-intl';
import { PlusOutlined } from '@ant-design/icons';
import { useTheme } from '@mui/material/styles';
import { DragDropContext, Draggable, Droppable, DropResult } from 'react-beautiful-dnd';
import { useHandleChangeTouchpointsPhaseDetail } from './helpers/useHandleChangeTouchpointsPhaseDetail';
import { useHandleReorderTouchpointsDetail } from './helpers/useHandleReorderTouchpointsDetail';
import { NameCell } from './widgets/name/NameCell';
import { MIN_NUMBER_OF_ACTIVITIES } from './DetailTable';
import slugify from 'slugify';
import { useModalContext } from '../../../modal/ModalContext';
import { useJourneyFilters } from '../../useJourneyFilters';
import { ThemeMode } from '../../../types/config';
import { getNightOrDayPalette } from '../../../utils/getNightOrDayPalette';
import { useHSMContext } from '../../HorizontalScrollMapContext';
import { AddActivityDividerButton } from '../../utils/AddActivityDividerButton';

type Props = Readonly<{
	journeyId: string;
	phases: readonly Phase[];
	isFirst?: boolean;
}>;

const NameRow: FC<Props> = ({ phases, isFirst, journeyId }) => {
	const theme = useTheme();
	const [state, setState] = useState(phases);
	const { setModal } = useModalContext();
	const { setIsVerticalScrollEnabled } = useHSMContext();
	const handleChangeTouchpointsPhase = useHandleChangeTouchpointsPhaseDetail({ state, setState });
	const handleReorderTouchpoints = useHandleReorderTouchpointsDetail({ state, setState });
	const [filters] = useJourneyFilters();
	const isDragDisabled = Object.keys(filters).length > 0;

	const odd = theme.palette.mode === ThemeMode.DARK ? theme.palette[getNightOrDayPalette(theme)][100] : '#F7F7F7';
	const even = theme.palette[getNightOrDayPalette(theme)][0];

	useEffect(() => {
		setState(phases);
	}, [phases]);

	const onDragEnd = async (result: DropResult) => {
		const { source, destination } = result;

		if (!destination) {
			return;
		}

		if (destination.droppableId === source.droppableId && destination.index === source.index) {
			return;
		}

		if (source.droppableId !== destination.droppableId) {
			await handleChangeTouchpointsPhase(result);
			setIsVerticalScrollEnabled(true);
			return;
		}

		if (source.droppableId === destination.droppableId) {
			await handleReorderTouchpoints(result);
			setIsVerticalScrollEnabled(true);
			return;
		}
	};

	const onDragStart = () => {
		setIsVerticalScrollEnabled(false);
	};

	return (
		<DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
			<tr style={{ position: 'sticky', top: 0, zIndex: 3 }}>
				<TableCell header isSticky>
					<FormattedMessage id={'journey.detail.activityName'} />
				</TableCell>

				{state.map((phase, index) => {
					const isLastPhase = index === phases.length - 1;
					const bgcolor = index % 2 === 0 ? even : odd;

					if (phase.touchpoints.length === 0 && isFirst === true) {
						return (
							<td
								key={phase.id}
								style={{
									borderRight: isLastPhase ? undefined : `2px solid transparent`,
									padding: 0,
								}}
							>
								<table
									style={{
										width: '100%',
										height: '100%',
										border: 'none',
										borderCollapse: 'collapse',
									}}
								>
									<Droppable key={phase.id} droppableId={phase.id} direction={'horizontal'} ignoreContainerClipping={false}>
										{(provided) => (
											<tr>
												<TableCell
													dndRef={provided.innerRef}
													{...provided.droppableProps}
													key={phase.id}
													rowSpan={0}
													sx={{ px: 3, backgroundColor: `${bgcolor} !important` }}
												>
													<Typography color={theme.palette.grey[500]} gutterBottom>
														<FormattedMessage id={'journey.detail.no-activity'} />
													</Typography>
													<Button
														variant={'outlined'}
														onClick={() => setModal({ type: 'createActivity', payload: { journeyId: journeyId, phaseId: phase.id } })}
														startIcon={<PlusOutlined />}
													>
														<FormattedMessage id={'journey.detail.create-activity'} />
													</Button>
													{provided.placeholder}
												</TableCell>
											</tr>
										)}
									</Droppable>
								</table>
							</td>
						);
					}

					if (phase.touchpoints.length === 0 && isFirst !== true) {
						return null;
					}

					const emptyCellsCount = MIN_NUMBER_OF_ACTIVITIES - phase.touchpoints.length;
					const emptyCells = Array.from({ length: emptyCellsCount }, () => null);

					return (
						<td
							key={phase.id}
							colSpan={phase.touchpoints.length}
							style={{
								borderLeft: isFirst ? '2px solid transparent' : undefined,
								borderRight: isLastPhase ? undefined : `2px solid transparent`,
								padding: 0,
							}}
						>
							<table style={{ width: '100%', height: '100%', border: 'none', borderCollapse: 'separate', borderSpacing: 0 }}>
								<Droppable key={phase.id} droppableId={phase.id} direction={'horizontal'} ignoreContainerClipping={false}>
									{(provided) => {
										return (
											<tr key={phase.id} ref={provided.innerRef} {...provided.droppableProps}>
												{phase.touchpoints.map(({ activity, id }, index) => {
													if (activity == null) {
														return null;
													}

													return (
														<Draggable key={id} draggableId={id} index={index} isDragDisabled={isDragDisabled}>
															{(provided) => (
																<TableCell
																	colSpan={emptyCellsCount > 0 && index === phase.touchpoints.length - 1 ? emptyCellsCount + 1 : 0}
																	dndRef={provided.innerRef}
																	{...provided.draggableProps}
																	{...provided.dragHandleProps}
																	key={id}
																	sx={{
																		minWidth: 290,
																		maxWidth: 290,
																		position: 'relative',
																		verticalAlign: 'top',
																		border: `1px solid ${theme.palette.grey[200]}`,
																		px: 3,
																		py: 2,
																		borderLeft: 'none',
																		bgcolor,
																		':last-of-type': {
																			borderRight: '1px solid transparent',

																			'.journey__activity-divider-button': {
																				right: '-18.5px',
																			},
																		},
																		':first-of-type': {
																			borderLeft: '1px solid transparent',
																		},
																	}}
																	data-activity={slugify(activity.name, { lower: true })}
																	data-activity-id={activity.id}
																>
																	<NameCell
																		activityId={activity.id}
																		touchpointId={id}
																		value={activity.name}
																		hasWarnings={activity.warnings.length > 0}
																		phaseId={phase.id}
																	/>
																	<AddActivityDividerButton phaseId={phase.id} index={index + 1} />
																</TableCell>
															)}
														</Draggable>
													);
												})}
												{provided.placeholder}

												{emptyCells.map((_, i) => {
													return (
														<TableCell
															key={i}
															sx={{
																minWidth: 290,
																maxWidth: 290,
																position: 'relative',
																verticalAlign: 'top',
																border: `none`,
																height: '100%',
																bgcolor,
															}}
														></TableCell>
													);
												})}
											</tr>
										);
									}}
								</Droppable>
							</table>
						</td>
					);
				})}
			</tr>
		</DragDropContext>
	);
};

export { NameRow };
